import { resourcesCopy } from '@/copy/resources'
import {
  CheckboxField,
  Field,
  SelectField,
  Textarea,
  TextField,
} from '@/ui/Field/Field'
import { Grid } from '@/ui/layout/Grid'
import { type FieldProps, type Fields, fieldStr } from './shared'

/**
 * Cron form, aligned with the backend handler's native fields:
 * recurring (`schedule` cron expression) or one-shot (`one_shot` +
 * `fire_at_ms`).
 */

/** Recurring-schedule input modes. */
type ScheduleMode = 'interval' | 'daily' | 'expression'

// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function CronFields({ fields, set }: FieldProps) {
  const isOneShot = fields.mode === 'one_shot'
  const schedMode = (fields.schedMode ?? 'interval') as ScheduleMode

  return (
    <>
      <SelectField
        id="res-cron-mode"
        label={resourcesCopy.forms.cron.fires}
        value={fields.mode ?? 'recurring'}
        onValue={(v) => set('mode', v)}
      >
        <option value="recurring">{resourcesCopy.forms.cron.recurring}</option>
        <option value="one_shot">{resourcesCopy.forms.cron.oneShot}</option>
      </SelectField>

      {isOneShot ? (
        <TextField
          id="res-cron-fire-at"
          label={resourcesCopy.forms.cron.fireAt}
          value={fields.fireAt ?? ''}
          onValue={(v) => set('fireAt', v)}
          inputProps={{ type: 'datetime-local', required: true }}
        />
      ) : (
        <>
          <SelectField
            id="res-cron-sched-mode"
            label={resourcesCopy.forms.cron.schedule}
            value={schedMode}
            onValue={(v) => set('schedMode', v)}
          >
            <option value="interval">
              {resourcesCopy.forms.cron.interval}
            </option>
            <option value="daily">{resourcesCopy.forms.cron.daily}</option>
            <option value="expression">
              {resourcesCopy.forms.cron.expression}
            </option>
          </SelectField>

          {schedMode === 'interval' && (
            <Grid columns={1} mdColumns={2} gap="space-16">
              <TextField
                id="res-cron-interval"
                label={resourcesCopy.forms.cron.every}
                value={fields.intervalValue ?? '30'}
                onValue={(v) => set('intervalValue', v)}
                inputProps={{
                  type: 'number',
                  min: 1,
                  max: fields.intervalUnit === 'hours' ? 23 : 59,
                  required: true,
                }}
              />
              <SelectField
                id="res-cron-interval-unit"
                label={resourcesCopy.forms.cron.unit}
                value={fields.intervalUnit ?? 'minutes'}
                onValue={(v) => set('intervalUnit', v)}
              >
                <option value="minutes">
                  {resourcesCopy.forms.cron.minutes}
                </option>
                <option value="hours">{resourcesCopy.forms.cron.hours}</option>
              </SelectField>
            </Grid>
          )}

          {schedMode === 'daily' && (
            <TextField
              id="res-cron-daily-at"
              label={resourcesCopy.forms.cron.timeOfDay}
              value={fields.dailyAt ?? '09:00'}
              onValue={(v) => set('dailyAt', v)}
              inputProps={{ type: 'time', required: true }}
            />
          )}

          {schedMode === 'expression' && (
            <TextField
              id="res-cron-expr"
              label={resourcesCopy.forms.cron.expression}
              hint={resourcesCopy.forms.cron.expressionHint}
              value={fields.schedule ?? ''}
              onValue={(v) => set('schedule', v)}
              inputProps={{
                placeholder: resourcesCopy.forms.cron.expressionPlaceholder,
                required: true,
                mono: true,
              }}
            />
          )}
        </>
      )}

      <Field id="res-cron-task" label={resourcesCopy.forms.cron.task}>
        <Textarea
          id="res-cron-task"
          value={fields.task ?? ''}
          onChange={(e) => set('task', e.target.value)}
          rows={4}
        />
      </Field>

      <TextField
        id="res-cron-event-type"
        label={resourcesCopy.forms.cron.eventType}
        hint={resourcesCopy.form.optional}
        value={fields.event_type ?? ''}
        onValue={(v) => set('event_type', v)}
        inputProps={{
          placeholder: resourcesCopy.forms.cron.eventTypePlaceholder,
        }}
      />

      {!isOneShot && (
        <Grid columns={1} mdColumns={2} gap="space-16">
          <TextField
            id="res-cron-hours"
            label={resourcesCopy.forms.cron.activeHours}
            hint={resourcesCopy.form.optional}
            value={fields.active_hours ?? ''}
            onValue={(v) => set('active_hours', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.cron.activeHoursPlaceholder,
            }}
          />
          <TextField
            id="res-cron-tz"
            label={resourcesCopy.forms.cron.timezone}
            hint={resourcesCopy.form.optional}
            value={fields.timezone ?? ''}
            onValue={(v) => set('timezone', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.cron.timezonePlaceholder,
            }}
          />
        </Grid>
      )}

      <CheckboxField
        id="res-cron-enabled"
        label={resourcesCopy.forms.cron.enabled}
        checked={fields.enabled !== 'false'}
        onChecked={(b) => set('enabled', String(b))}
      />
    </>
  )
}

/** Epoch ms → `datetime-local` input value (local time, minute precision). */
export function toLocalInputValue(ms: number): string {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Derive the cron expression for the current recurring sub-mode. */
export function scheduleFromFields(f: Fields): string {
  switch (f.schedMode ?? 'interval') {
    case 'daily': {
      const [h = '9', m = '0'] = (f.dailyAt ?? '09:00').split(':')
      return `${Number(m) || 0} ${Number(h) || 9} * * *`
    }
    case 'expression':
      return (f.schedule ?? '').trim()
    default: {
      const n = Math.max(1, Math.floor(Number(f.intervalValue) || 30))
      return f.intervalUnit === 'hours'
        ? `0 */${Math.min(n, 23)} * * *`
        : `*/${Math.min(n, 59)} * * * *`
    }
  }
}

/** The request `data` body: only backend-native fields. */
export const cronForm = {
  Fields: CronFields,
  toBody: (f: Fields) => {
    const enabled = f.enabled !== 'false'
    const common = {
      task: f.task ?? '',
      enabled,
      ...(f.event_type ? { event_type: f.event_type } : {}),
    }
    if (f.mode === 'one_shot') {
      const ms = Date.parse(f.fireAt ?? '')
      return {
        ...common,
        one_shot: true,
        fire_at_ms: Number.isNaN(ms) ? Date.now() : ms,
      }
    }
    return {
      ...common,
      schedule: scheduleFromFields(f),
      ...(f.active_hours ? { active_hours: f.active_hours } : {}),
      ...(f.timezone ? { timezone: f.timezone } : {}),
    }
  },
  fromResource: (d: unknown): Fields => {
    const r = (d ?? {}) as Record<string, unknown>
    if (r.one_shot === true) {
      const ms = typeof r.fire_at_ms === 'number' ? r.fire_at_ms : Date.now()
      return {
        mode: 'one_shot',
        fireAt: toLocalInputValue(ms),
        task: fieldStr(d, 'task'),
        event_type: fieldStr(d, 'event_type'),
        enabled: String(r.enabled !== false),
      }
    }
    return {
      mode: 'recurring',
      schedMode: 'expression',
      schedule: fieldStr(d, 'schedule'),
      task: fieldStr(d, 'task'),
      event_type: fieldStr(d, 'event_type'),
      active_hours: fieldStr(d, 'active_hours'),
      timezone: fieldStr(d, 'timezone'),
      enabled: String(r.enabled !== false),
    }
  },
}
